import React, { useState, useRef } from 'react';
import { auth, storage } from '../../firebaseConfig';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import './VideoUploader.css';

function VideoUploader({ onVideoUploaded, onCancel }) {
    const [videoFile, setVideoFile] = useState(null);
    const [videoPreview, setVideoPreview] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [error, setError] = useState('');
    const fileInputRef = useRef(null);
    const videoRef = useRef(null);

    // Supported video formats für optimale Kompatibilität
    const SUPPORTED_FORMATS = [
        'video/mp4',
        'video/webm', 
        'video/ogg',
        'video/avi',
        'video/mov',
        'video/wmv'
    ];

    // Max file size: 500MB (kann angepasst werden)
    const MAX_FILE_SIZE = 500 * 1024 * 1024; // 500MB

    const validateVideoFile = (file) => {
        if (!file) return false;

        // Check file type
        if (!SUPPORTED_FORMATS.includes(file.type)) {
            setError(`Unsupported format. Please use: ${SUPPORTED_FORMATS.join(', ')}`);
            return false;
        }

        // Check file size
        if (file.size > MAX_FILE_SIZE) {
            setError(`File too large. Maximum size: ${MAX_FILE_SIZE / (1024 * 1024)}MB`);
            return false;
        }

        return true;
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!validateVideoFile(file)) return;

        setVideoFile(file);
        setError('');

        // Create video preview
        const videoUrl = URL.createObjectURL(file);
        setVideoPreview(videoUrl);
    };

    const removeVideo = () => {
        setVideoFile(null);
        setVideoPreview(null);
        setUploadProgress(0);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const optimizeFileName = (fileName) => {
        // Entferne Sonderzeichen und Leerzeichen für bessere Kompatibilität
        return fileName
            .toLowerCase()
            .replace(/[^a-z0-9.-]/g, '_')
            .replace(/_{2,}/g, '_');
    };

    const uploadVideo = async () => {
        if (!videoFile || !auth.currentUser) return;

        setUploading(true);
        setUploadProgress(0);

        try {
            const timestamp = Date.now();
            const optimizedName = optimizeFileName(videoFile.name);
            const fileName = `academy/videos/${timestamp}_${optimizedName}`;
            
            const storageRef = ref(storage, fileName);
            
            // Resumable upload for large files
            const uploadTask = uploadBytesResumable(storageRef, videoFile, {
                contentType: videoFile.type,
                customMetadata: {
                    'originalName': videoFile.name,
                    'fileSize': videoFile.size.toString(),
                    'uploadedBy': auth.currentUser.uid,
                    'uploadedAt': new Date().toISOString()
                }
            });

            // Monitor upload progress
            uploadTask.on('state_changed', 
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    setUploadProgress(Math.round(progress));
                    
                    switch (snapshot.state) {
                        case 'paused':
                            console.log('Upload is paused');
                            break;
                        case 'running':
                            console.log('Upload is running');
                            break;
                        default:
                            break;
                    }
                }, 
                (error) => {
                    console.error('Upload error:', error);
                    let errorMessage = 'Upload failed. Please try again.';
                    
                    switch (error.code) {
                        case 'storage/unauthorized':
                            errorMessage = 'You do not have permission to upload files.';
                            break;
                        case 'storage/canceled':
                            errorMessage = 'Upload was canceled.';
                            break;
                        case 'storage/quota-exceeded':
                            errorMessage = 'Storage quota exceeded.';
                            break;
                        case 'storage/retry-limit-exceeded':
                            errorMessage = 'Upload timeout. Please check your connection.';
                            break;
                        default:
                            errorMessage = `Upload error: ${error.message}`;
                    }
                    
                    setError(errorMessage);
                    setUploading(false);
                }, 
                async () => {
                    // Upload completed successfully
                    try {
                        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                        
                        // Get video metadata
                        const videoMetadata = {
                            fileName: optimizedName,
                            originalName: videoFile.name,
                            fileSize: videoFile.size,
                            fileType: videoFile.type,
                            downloadURL: downloadURL,
                            storagePath: fileName,
                            duration: videoRef.current?.duration || 0,
                            width: videoRef.current?.videoWidth || 0,
                            height: videoRef.current?.videoHeight || 0,
                            uploadedAt: new Date().toISOString()
                        };

                        onVideoUploaded(videoMetadata);
                        
                    } catch (error) {
                        console.error('Error getting download URL:', error);
                        setError('Failed to finalize upload. Please try again.');
                    } finally {
                        setUploading(false);
                    }
                }
            );

        } catch (error) {
            console.error('Upload initialization error:', error);
            setError('Failed to start upload. Please try again.');
            setUploading(false);
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatDuration = (seconds) => {
        if (!seconds || isNaN(seconds)) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <div className="video-uploader-container">
            <div className="video-uploader-header">
                <h3>Upload Learning Video</h3>
                <p>Upload high-quality videos for your academy</p>
            </div>

            <div className="video-uploader-content">
                {error && <div className="error-message">{error}</div>}

                {!videoFile ? (
                    <div className="video-upload-area" onClick={() => fileInputRef.current?.click()}>
                        <div className="upload-placeholder">
                            <div className="upload-icon">🎬</div>
                            <h4>Choose Video File</h4>
                            <p>Click to select or drag & drop</p>
                            <small>
                                Supported: MP4, WebM, OGG, AVI, MOV, WMV
                                <br />
                                Max size: {MAX_FILE_SIZE / (1024 * 1024)}MB
                            </small>
                        </div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="video/*"
                            onChange={handleFileSelect}
                            className="hidden-file-input"
                        />
                    </div>
                ) : (
                    <div className="video-preview-section">
                        <div className="video-preview">
                            <video
                                ref={videoRef}
                                src={videoPreview}
                                controls
                                className="preview-video"
                                onLoadedMetadata={() => {
                                    // Video metadata loaded
                                    if (videoRef.current) {
                                        console.log('Video duration:', videoRef.current.duration);
                                        console.log('Video dimensions:', videoRef.current.videoWidth, 'x', videoRef.current.videoHeight);
                                    }
                                }}
                            />
                            <button 
                                className="remove-video-btn"
                                onClick={removeVideo}
                                disabled={uploading}
                            >
                                ✕
                            </button>
                        </div>

                        <div className="video-info">
                            <h4>{videoFile.name}</h4>
                            <div className="video-details">
                                <span>Size: {formatFileSize(videoFile.size)}</span>
                                <span>Type: {videoFile.type}</span>
                                {videoRef.current?.duration && (
                                    <span>Duration: {formatDuration(videoRef.current.duration)}</span>
                                )}
                            </div>
                        </div>

                        {uploading && (
                            <div className="upload-progress">
                                <div className="progress-bar">
                                    <div 
                                        className="progress-fill"
                                        style={{ width: `${uploadProgress}%` }}
                                    />
                                </div>
                                <span className="progress-text">
                                    Uploading... {uploadProgress}%
                                </span>
                            </div>
                        )}
                    </div>
                )}

                <div className="uploader-actions">
                    <button 
                        onClick={onCancel}
                        className="cancel-btn"
                        disabled={uploading}
                    >
                        Cancel
                    </button>
                    <button 
                        onClick={uploadVideo}
                        className="upload-btn"
                        disabled={!videoFile || uploading}
                    >
                        {uploading ? 'Uploading...' : 'Upload Video'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default VideoUploader;
